import { Icon } from '../../../components/icons/Icon'
import { StatusPill } from '../../../components/StatusPill'
import { lifecycleStatusPill, type LifecycleStatus } from './lifecycleStatus'
import './MakerCheckerBar.css'

/**
 * UX design-stage scaffold (IB-210 Slice 2, demo v66 — see
 * `docs/design/IB-210-cartridge-studio-ui-design.md` §5 for the full state
 * matrix this implements). PRESENTATIONAL ONLY: no `fetch`, no routing, no
 * internal state — every value and callback comes from props. The Developer
 * wires this to the real `review-submissions` / `approvals` / `publications`
 * endpoints (`RuleSetController` / `ParameterController` /
 * `EligibilityModelController`) and to `CalculationGraphController.create`
 * for the `direct-publish` variant, and adds the Vitest/RTL + a11y coverage
 * named in the DoR (`docs/stories/IB-210-cartridge-studio-ui-DoR.md` §4).
 *
 * Two variants, ONE component — because the underlying state machine really
 * is shared identically by `RuleSet`/`Parameter`/`EligibilityModel`
 * (ADR-0054), and `CalculationGraph` really has no draft/review/approve step
 * at all (ADR-0020) rather than a degenerate case of the same machine.
 */
export type MakerCheckerVariant = 'lifecycle' | 'direct-publish'

export interface MakerCheckerBarProps {
  /** Human label for the governed artifact, e.g. "RuleSet v3", "EligibilityModel v2". */
  artifactLabel: string
  variant: MakerCheckerVariant
  /** Ignored when `variant="direct-publish"`. */
  status?: LifecycleStatus
  /** True when the signed-in user authored the current draft — drives the maker≠checker disablement
   * (design-system.md §4.9 "disabled-with-reason" pattern: `aria-disabled`, stays focusable, never a
   * native `disabled` — the real enforcement is the API's 403, this is the courtesy). */
  isAuthor?: boolean
  busy?: boolean
  /** The last transition attempt's problem-detail message (403/409/422), rendered inline — never
   * swallowed (DoR AC5/AC5b/AC6's explicit "surfaced inline" failure paths). */
  errorMessage?: string | null
  onSubmitForReview?: () => void
  onApprove?: () => void
  onPublish?: () => void
  onNewVersion?: () => void
}

const STEP_ORDER: LifecycleStatus[] = ['draft', 'review', 'approved', 'published']
const STEP_LABEL: Record<LifecycleStatus, string> = {
  draft: 'Draft',
  review: 'Review',
  approved: 'Approved',
  published: 'Published',
}

export function MakerCheckerBar(props: MakerCheckerBarProps) {
  if (props.variant === 'direct-publish') {
    return (
      <div className="iw-mcbar iw-mcbar--direct" role="group" aria-label={`${props.artifactLabel} publish status`}>
        <div className="iw-mcbar__title">
          <Icon name="check-circle" size={16} />
          {props.artifactLabel}
          <StatusPill variant="success" label="published & pinned" />
        </div>
        <div className="iw-mcbar__note">
          <Icon name="info" size={13} />
          ADR-0020 — this artifact publishes directly on save. There is no draft/review/approve step,
          and the fixture runner below is informational only — it never blocks this action.
        </div>
        {props.onNewVersion ? (
          <div className="iw-mcbar__actions">
            <button type="button" className="iw-btn iw-btn--primary" onClick={props.onNewVersion} disabled={props.busy}>
              New version
            </button>
          </div>
        ) : null}
        {props.errorMessage ? <ErrorRow message={props.errorMessage} /> : null}
      </div>
    )
  }

  const status = props.status ?? 'draft'
  const stepIndex = STEP_ORDER.indexOf(status)
  const isAuthor = Boolean(props.isAuthor)
  const showReason = isAuthor && (status === 'review' || status === 'approved')

  return (
    <div className="iw-mcbar" role="group" aria-label={`${props.artifactLabel} publish workflow`}>
      <div className="iw-mcbar__title">
        <Icon name="check-circle" size={16} />
        {props.artifactLabel}
        <StatusPill {...lifecycleStatusPill(status)} />
      </div>

      <ol className="iw-mcbar__steps" aria-label="Publish workflow">
        {STEP_ORDER.map((step, i) => {
          const cls =
            i < stepIndex
              ? 'iw-mcbar__step iw-mcbar__step--done'
              : i === stepIndex
                ? 'iw-mcbar__step iw-mcbar__step--active'
                : 'iw-mcbar__step'
          return (
            <li key={step} className={cls}>
              <span className="iw-mcbar__dot" aria-hidden="true">
                {i < stepIndex ? <Icon name="check" size={11} /> : i + 1}
              </span>
              {STEP_LABEL[step]}
            </li>
          )
        })}
      </ol>

      <div className="iw-mcbar__actions">
        {status === 'draft' && props.onSubmitForReview ? (
          <button type="button" className="iw-btn iw-btn--primary" onClick={props.onSubmitForReview} disabled={props.busy}>
            Submit for review
          </button>
        ) : null}
        {status === 'review' ? (
          <button
            type="button"
            className="iw-btn iw-btn--primary"
            aria-disabled={isAuthor || undefined}
            disabled={props.busy}
            onClick={isAuthor ? undefined : props.onApprove}
            aria-describedby={isAuthor ? 'iw-mcbar-reason' : undefined}
          >
            Approve
          </button>
        ) : null}
        {status === 'approved' ? (
          <button
            type="button"
            className="iw-btn iw-btn--primary"
            aria-disabled={isAuthor || undefined}
            disabled={props.busy}
            onClick={isAuthor ? undefined : props.onPublish}
            aria-describedby={isAuthor ? 'iw-mcbar-reason' : undefined}
          >
            Publish
          </button>
        ) : null}
        {status === 'published' && props.onNewVersion ? (
          <button type="button" className="iw-btn iw-btn--ghost" onClick={props.onNewVersion} disabled={props.busy}>
            Author new version
          </button>
        ) : null}
      </div>

      {showReason ? (
        <div className="iw-mcbar__reason" id="iw-mcbar-reason" role="status">
          <Icon name="alert-circle" size={13} />
          {status === 'review' ? 'Approve' : 'Publish'} disabled — you authored this draft (maker ≠ checker).
          Sign in as a different qualified user to continue.
        </div>
      ) : null}

      {props.errorMessage ? <ErrorRow message={props.errorMessage} /> : null}
    </div>
  )
}

function ErrorRow({ message }: { message: string }) {
  return (
    <div className="iw-mcbar__error" role="alert">
      <Icon name="alert-circle" size={13} />
      {message}
    </div>
  )
}
